import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  useDeferredValue,
  Suspense,
} from "react";
import { Rnd } from "react-rnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faLink } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import {
  WindowCloseIcon,
  WindowMinusIcon,
  WindowPlusIcon,
  WindowRestoreIcon,
} from "./OsWindowControlIcons";
import {
  useOsActions,
  useWindowById,
  type OsWindow as OsWindowType,
} from "./OsContext";
import { osInteractionRef } from "./osInteractionRef";
import { WindowIdContext } from "./windowIdContext";
import {
  getOsWindowShellClassName,
  getOsWindowSuspendedOverlayClassName,
  OS_WINDOW_TITLEBAR_BUTTON_CLASS,
  OS_WINDOW_TITLEBAR_BUTTON_ICON_CLASS,
  OS_WINDOW_TITLEBAR_BUTTONS_CLASS,
} from "./osWindowChrome";
import { WindowActiveProvider } from "./WindowActiveContext";
import {
  APP_REGISTRY,
  canAccessWindow,
  type OpenWindowParams,
} from "./OsAppRegistry";
import type { OpenWindowConfig } from "./useWindow";
import { useAuth } from "@/context/AuthContext";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import { toAbsoluteUrl } from "@/navigation/absoluteUrl";
import { buildWindowUrl } from "./urlMapping";
import { useNotificationOptional } from "@/context/NotificationContext";
import { ChunkLoadAwareErrorBoundary } from "@/components/error/ChunkLoadAwareErrorBoundary";
import { WindowLoadingFallback } from "./WindowLoadingFallback";
import { Button } from "@/components/ui/button/Button";
import { usePlatform } from "@/hooks/usePlatform";
import { getShortcutLabel } from "./osShortcuts";
import "./OsWindow.css";

const WORKSPACE_SUSPEND_MS = 30_000;

/**
 * Context to provide the OS openWindow action scoped to the current window tree.
 */
const OsOpenWindowContext = createContext<
  ((config: OpenWindowConfig) => void) | undefined
>(undefined);

export { useWindowId } from "./windowIdContext";

/**
 * Hook to open a new OS window from inside the current window's content tree.
 */
export const useOsOpenWindowOptional = ():
  | ((config: OpenWindowConfig) => void)
  | undefined => {
  return useContext(OsOpenWindowContext);
};

interface OsWindowProps {
  window: OsWindowType;
  isActive?: boolean;
  /** Whether this window is the focused (topmost) window in the workspace. */
  isFocused?: boolean;
}

const OsWindowComponent: React.FC<OsWindowProps> = ({
  window,
  isActive = true,
  isFocused: isFocusedProp = true,
}) => {
  const { closeWindow, requestCloseWindow, focusWindow, updateWindow, openWindow, setWindowInteracting } =
    useOsActions();
  const { hasPermission, permissions, isLoading: permissionsLoading } =
    useModulePermissions();
  const { user, isLoading: authLoading } = useAuth();
  const notification = useNotificationOptional();
  const { isMac } = usePlatform();
  const isWindowFocused = isFocusedProp;
  const nodeRef = useRef<Rnd | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [workspaceContentSuspended, setWorkspaceContentSuspended] = useState(false);

  const isInActiveWorkspace = isActive;
  // Gates TanStack Query + WS in child apps — only the focused, non-minimised window is "live".
  const isWindowLive =
    isInActiveWorkspace && !window.isMinimized && isWindowFocused;

  // Stable value so useWindowLive() consumers only re-render when live state flips,
  // not on every chrome re-render (drag start/stop, title change, etc.).
  const windowActiveValue = React.useMemo(
    () => ({ isInActiveWorkspace, isWindowLive }),
    [isInActiveWorkspace, isWindowLive],
  );

  useEffect(() => {
    if (isInActiveWorkspace) {
      setWorkspaceContentSuspended(false);
      return;
    }
    const timer = setTimeout(
      () => setWorkspaceContentSuspended(true),
      WORKSPACE_SUSPEND_MS,
    );
    return () => clearTimeout(timer);
  }, [isInActiveWorkspace]);

  const appDef = APP_REGISTRY[window.appId];
  const isResizable = appDef?.isResizable !== false;
  const openWindowCallback = useCallback(
    (config: OpenWindowConfig) => openWindow(config),
    [openWindow],
  );

  // Defer title updates to lower priority - avoids blocking drag operations
  const deferredTitle = useDeferredValue(window.title);

  // Check if user has permission to access this window's content
  const accessCheckReady = !authLoading && !permissionsLoading;
  const hasAccess = canAccessWindow(
    window.appId,
    hasPermission,
    user,
    permissions,
  );

  // Minimized windows and long-idle inactive workspaces unmount content for memory.
  const shouldUnmountContent =
    window.isMinimized || workspaceContentSuspended;

  // Use refs for values that shouldn't cause content re-creation
  // This preserves component state (like selected tabs) when interacting with the window
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  
  const openWindowCallbackRef = useRef(openWindowCallback);
  openWindowCallbackRef.current = openWindowCallback;

  // Stable callback that doesn't change identity
  const stableOpenWindowCallback = useCallback(
    (config: OpenWindowParams) => openWindowCallbackRef.current(config),
    []
  );

  // Create content only once per window - use refs for all values that might change
  // This prevents remounting of child components when interacting with the window
  const renderedContent = React.useMemo(() => {
    if (shouldUnmountContent) return null;

    if (!hasAccess) {
      if (!accessCheckReady) {
        return (
          <WindowIdContext.Provider value={window.id}>
            <OsOpenWindowContext.Provider value={openWindowCallback}>
              <WindowLoadingFallback />
            </OsOpenWindowContext.Provider>
          </WindowIdContext.Provider>
        );
      }

      return (
        <WindowIdContext.Provider value={window.id}>
          <OsOpenWindowContext.Provider value={openWindowCallback}>
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <FontAwesomeIcon
              icon={faLock}
              className="text-5xl text-slate-400 mb-4"
            />
            <h2 className="text-xl font-semibold text-slate-200 mb-2">
              Access Denied
            </h2>
            <p className="text-slate-400 max-w-md">
              You don&apos;t have permission to access this application. Please
              contact your administrator if you believe this is an error.
            </p>
          </div>
          </OsOpenWindowContext.Provider>
        </WindowIdContext.Provider>
      );
    }

    // Pass isActive via the ref's current value - apps that need real-time isActive
    // updates should use the WindowActiveContext instead
    let content: React.ReactNode = null;
    if (import.meta.env.DEV) {
      const start = performance.now();
      content =
        appDef?.render(window.params, stableOpenWindowCallback, isActiveRef.current) ?? null;
      const elapsed = performance.now() - start;
      if (elapsed > 16) {
        console.warn(
          `[Perf] Window "${window.title}" (${window.appId}) render took ${elapsed.toFixed(1)}ms`,
        );
      }
    } else {
      content =
        appDef?.render(window.params, stableOpenWindowCallback, isActiveRef.current) ?? null;
    }

    return (
      <WindowIdContext.Provider value={window.id}>
        <OsOpenWindowContext.Provider value={openWindowCallback}>
          {content}
        </OsOpenWindowContext.Provider>
      </WindowIdContext.Provider>
    );
    // Minimal dependencies - only things that fundamentally change what content to render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appDef,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(window.params),
    shouldUnmountContent,
    window.id,
    hasAccess,
    window.appId,
    hasAccess,
    accessCheckReady,
  ]);

  // Trigger entrance animation on mount
  useEffect(() => {
    // Use requestAnimationFrame to ensure the initial state is painted first
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsMounted(true);
      });
    });
  }, []);

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateWindow(window.id, { isMinimized: !window.isMinimized });
  };

  const handleMaximize = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isResizable) return;
    updateWindow(window.id, { isMaximized: !window.isMaximized });
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!requestCloseWindow(window.id)) return;
    setIsClosing(true);
    // Delay actual close to allow animation to complete
    setTimeout(() => {
      closeWindow(window.id);
    }, 100);
  };

  const handleShare = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const url = toAbsoluteUrl(buildWindowUrl(window.appId, window.params));
      navigator.clipboard.writeText(url).then(() => {
        notification?.showSuccess(
          "Link copied",
          "The link has been copied to your clipboard",
        );
      });
    },
    [window.appId, window.params, notification],
  );

  const handleMouseDown = useCallback(() => {
    // Skip focus updates during global interaction to avoid state cascades
    if (osInteractionRef.current) return;
    focusWindow(window.id);
  }, [focusWindow, window.id]);

  // Calculate position and size based on maximized state
  const isWindowMaximized = isResizable && window.isMaximized;
  const position = isWindowMaximized ? { x: 0, y: 0 } : window.position;
  const size = isWindowMaximized
    ? { width: "100%", height: "100%" }
    : window.size;

  // Animation state for entrance and exit
  const animationStyle = isClosing
    ? { opacity: 0, transform: "scale(0.96) translateY(10px)" }
    : isMounted
      ? { opacity: 1, transform: "scale(1) translateY(0px)" }
      : { opacity: 0, transform: "scale(0.96) translateY(10px)" };

  const isInteracting = isDragging || isResizing;

  // Don't render minimized windows
  if (window.isMinimized) {
    return null;
  }

  return (
    <Rnd
      key={window.id}
      ref={(rndInstance) => {
        nodeRef.current = rndInstance;
      }}
      position={position}
      size={size}
      scale={1}
      onDragStart={() => {
        setIsDragging(true);
        setWindowInteracting(window.id, true);
      }}
      onDragStop={(e, d) => {
        // Update position first (synchronously), then clear interaction state
        // This prevents the transition from animating the final position update
        // Round to integers - backend requires integer positions
        if (!isWindowMaximized) {
          updateWindow(window.id, { position: { x: Math.round(d.x), y: Math.round(d.y) } });
        }
        // Clear state after position is committed
        setIsDragging(false);
        setWindowInteracting(window.id, false);
      }}
      onResizeStart={() => {
        setIsResizing(true);
        setWindowInteracting(window.id, true);
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        // Update size/position first, then clear interaction state
        // Round position to integers - backend requires integer positions
        if (!isWindowMaximized) {
          updateWindow(window.id, {
            size: {
              width: parseInt(ref.style.width),
              height: parseInt(ref.style.height),
            },
            position: { x: Math.round(position.x), y: Math.round(position.y) },
          });
        }
        // Clear state after size is committed
        setIsResizing(false);
        setWindowInteracting(window.id, false);
      }}
      minWidth={window.minSize?.width || 200}
      minHeight={window.minSize?.height || 40}
      bounds="parent"
      dragHandleClassName="window-title-bar"
      enableResizing={isResizable && !isWindowMaximized && !window.isMinimized}
      disableDragging={isWindowMaximized}
      dragAxis="both"
      enableUserSelectHack={true}
      resizeHandleStyles={{
        bottom: { height: "8px", bottom: "0px", cursor: "ns-resize", zIndex: 50 },
        right: { width: "8px", right: "0px", cursor: "ew-resize", zIndex: 50 },
        bottomRight: {
          width: "16px",
          height: "16px",
          bottom: "0px",
          right: "0px",
          cursor: "nwse-resize",
          zIndex: 50,
        },
        bottomLeft: {
          width: "16px",
          height: "16px",
          bottom: "0px",
          left: "0px",
          cursor: "nesw-resize",
          zIndex: 50,
        },
        topRight: {
          width: "20px",
          height: "20px",
          top: "0px",
          right: "0px",
          cursor: "nesw-resize",
          zIndex: 50,
        },
        topLeft: {
          width: "20px",
          height: "20px",
          top: "0px",
          left: "0px",
          cursor: "nwse-resize",
          zIndex: 50,
        },
        top: { height: "8px", top: "0px", cursor: "ns-resize", zIndex: 50 },
        left: { width: "8px", left: "0px", cursor: "ew-resize", zIndex: 50 },
      }}
      style={{
        zIndex: window.zIndex,
        display: "flex",
        flexDirection: "column",
        transformOrigin: "center center",
        willChange: isInteracting ? "transform" : "auto",
        // Avoid `contain: paint` — it conflicts with `overflow-visible` on the shell
        // and causes black paint artifacts when combined with content-visibility.
        contain: "layout style",
        transition: isInteracting
          ? "none"
          : "opacity 0.1s cubic-bezier(0.4, 0, 0.2, 1), transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), width 0.22s cubic-bezier(0.4, 0, 0.2, 1), height 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "absolute",
        ...animationStyle,
      }}
      className={getOsWindowShellClassName(
        isWindowFocused && isInActiveWorkspace,
      )}
      onMouseDown={handleMouseDown}
      data-window-id={window.id}
    >
      {/* ANI Working Indicator - Overlay */}
      <div
        data-testid="ani-working-overlay"
        data-active={window.isAniWorking ? "true" : "false"}
        className={`absolute inset-0 z-[100] ${
          window.isAniWorking ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{
          borderRadius: "calc(1.5rem - 1px)",
          transition: "opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center group/ani-overlay transition-all duration-300 bg-transparent hover:bg-slate-800/90">
          <div
            className="absolute inset-0 z-20 ani-glow"
            style={{
              borderRadius: "calc(1.5rem - 1px)",
              backgroundColor: "rgba(15, 23, 42, 0.05)",
            }}
          />
          <div className="opacity-0 group-hover/ani-overlay:opacity-100 z-30 relative transition-opacity duration-300 flex items-center gap-4">
            <div className="flex items-center flex-col gap-3">
              <span className="text-lg text-slate-200">
                Ani is in control of this window...
              </span>
              <Button
                data-testid="ani-working-stop-button"
                onClick={() => {
                  updateWindow(window.id, { isAniWorking: false });
                }}
              >
                Stop
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Title Bar */}
      <div
        className="window-title-bar relative z-30 flex min-h-10 flex-shrink-0 items-center justify-between px-2 py-2 cursor-move select-none overflow-visible"
        onDoubleClick={isResizable ? handleMaximize : undefined}
      >
        <div className="relative flex min-w-0 flex-1 items-center gap-2 px-4 text-base font-medium leading-none text-slate-700 dark:text-slate-200">
          <span className="truncate">{deferredTitle}</span>
          <button
            onClick={handleShare}
            className="opacity-0 group-hover/window:opacity-100 transition-opacity duration-200 flex items-center justify-center hover:bg-slate-100 cursor-pointer dark:hover:bg-slate-800 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors size-6 overflow-visible"
            title="Copy link"
          >
            <FontAwesomeIcon icon={faLink} className="size-4" />
          </button>
        </div>
        <div className={`z-20 ${OS_WINDOW_TITLEBAR_BUTTONS_CLASS}`}>
          {isResizable && (
            <button
              type="button"
              onClick={handleMaximize}
              className={OS_WINDOW_TITLEBAR_BUTTON_CLASS}
              title={window.isMaximized ? "Restore" : "Maximize"}
            >
              {window.isMaximized ? (
                <WindowRestoreIcon className={OS_WINDOW_TITLEBAR_BUTTON_ICON_CLASS} />
              ) : (
                <WindowPlusIcon className={OS_WINDOW_TITLEBAR_BUTTON_ICON_CLASS} />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={handleMinimize}
            className={OS_WINDOW_TITLEBAR_BUTTON_CLASS}
            title={window.isMinimized ? "Restore" : "Minimize"}
          >
            <WindowMinusIcon className={OS_WINDOW_TITLEBAR_BUTTON_ICON_CLASS} />
          </button>
          <button
            type="button"
            onClick={handleClose}
            className={OS_WINDOW_TITLEBAR_BUTTON_CLASS}
            title={`Close (${getShortcutLabel("CLOSE_ACTIVE_WINDOW", isMac)})`}
          >
            <WindowCloseIcon className={OS_WINDOW_TITLEBAR_BUTTON_ICON_CLASS} />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div
        className="flex-1 relative min-h-0 overflow-hidden rounded-b-3xl"
        style={{
          pointerEvents: isInteracting ? "none" : "auto",
        }}
        data-window-content="true"
      >
        <div
          className="relative h-full overflow-auto"
          data-testid="window-content-scroll"
          style={{
            // Unfocused windows in the active workspace stay painted (e.g. side-by-side).
            // Query/WS gating uses isWindowLive instead.
            contentVisibility: isInActiveWorkspace ? "visible" : "hidden",
          }}
        >
        <ChunkLoadAwareErrorBoundary variant="window">
          <Suspense fallback={<WindowLoadingFallback />}>
            {renderedContent && (
              <WindowActiveProvider value={windowActiveValue}>
                {renderedContent}
              </WindowActiveProvider>
            )}
          </Suspense>
        </ChunkLoadAwareErrorBoundary>

        {/* Suspension overlay */}
        {!isActive && workspaceContentSuspended && (
          <div
            className={getOsWindowSuspendedOverlayClassName()}
            style={{ isolation: "isolate" }}
          />
        )}
        </div>
      </div>
    </Rnd>
  );
};

export const OsWindow = React.memo(
  OsWindowComponent,
  (prevProps, nextProps) => {
    // Quick checks first (most likely to change)
    if (prevProps.window.zIndex !== nextProps.window.zIndex) return false;
    if (prevProps.window.isMinimized !== nextProps.window.isMinimized)
      return false;
    if (prevProps.window.isMaximized !== nextProps.window.isMaximized)
      return false;
    if (prevProps.isActive !== nextProps.isActive) return false;
    if (prevProps.isFocused !== nextProps.isFocused) return false;
    if (prevProps.window.isAniWorking !== nextProps.window.isAniWorking)
      return false;

    // Title check - must re-render when title changes
    if (prevProps.window.title !== nextProps.window.title) return false;

    // Position and size checks
    if (
      prevProps.window.position.x !== nextProps.window.position.x ||
      prevProps.window.position.y !== nextProps.window.position.y
    )
      return false;
    if (
      prevProps.window.size.width !== nextProps.window.size.width ||
      prevProps.window.size.height !== nextProps.window.size.height
    )
      return false;

    // Identity checks (least likely to change)
    if (
      prevProps.window.id !== nextProps.window.id ||
      prevProps.window.appId !== nextProps.window.appId
    )
      return false;

    // Params comparison (more expensive, do last)
    const prevParams = prevProps.window.params;
    const nextParams = nextProps.window.params;
    if (Object.keys(prevParams).length !== Object.keys(nextParams).length)
      return false;
    for (const key in prevParams) {
      if (prevParams[key] !== nextParams[key]) return false;
    }

    return true;
  },
);

/**
 * Wrapper component that subscribes to a specific window by ID.
 * This allows OsDesktop to only pass window IDs, avoiding re-renders
 * when other windows change position/size.
 *
 * Uses useMemo with a custom comparison to only re-render when THIS window's data changes.
 */
interface OsWindowByIdProps {
  windowId: string;
  isActive?: boolean;
  focusedWindowId: string | null;
}

const OsWindowByIdComponent: React.FC<OsWindowByIdProps> = ({
  windowId,
  isActive = true,
  focusedWindowId,
}) => {
  const windowData = useWindowById(windowId);

  if (!windowData) {
    return null;
  }

  return (
    <OsWindow
      window={windowData}
      isActive={isActive}
      isFocused={focusedWindowId === windowId}
    />
  );
};

export const OsWindowById = React.memo(OsWindowByIdComponent);
